import React, { useEffect, useState } from "react";
import { bookSlot, updatePaymentStatus, getHDFCOrderStatus, verifyHDFCReturn } from "../Services/api";
import Loader from "./Loader";

/**
 * Handles the HDFC SmartGateway return redirect.
 *
 * Flow:
 *  1. Read order_id from URL params
 *  2. Verify payment via backend Order Status API
 *  3. If CHARGED: restore pending booking from sessionStorage, call bookSlot + updatePaymentStatus
 *  4. Show result, redirect home after success
 */
const PaymentCallback = () => {
  const [status, setStatus] = useState("verifying"); // verifying | success | failed
  const [message, setMessage] = useState("");

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const rawParams = new URLSearchParams(window.location.search);
      const orderId = rawParams.get("order_id");

      if (!orderId) {
        setStatus("failed");
        setMessage(
          "Payment reference not found in the return URL. Please contact support."
        );
        return;
      }

      // Step 1: Verify HMAC signature on return URL params (server-side check)
      const allParams = Object.fromEntries(rawParams.entries());
      await verifyHDFCReturn(allParams);

      // Step 2: Verify actual payment status via Order Status API
      const orderStatus = await getHDFCOrderStatus(orderId);

      if (!orderStatus?.isPaid) {
        setStatus("failed");
        setMessage(
          `Payment was not completed (status: ${
            orderStatus?.status || "unknown"
          }). Please try again or pay at the counter.`
        );
        return;
      }

      // Restore pending booking state saved before redirect
      const pendingRaw = sessionStorage.getItem("hdfcPendingBooking");
      if (!pendingRaw) {
        setStatus("failed");
        setMessage(
          `Payment received (ref: ${orderId}) but session expired — booking could not be completed automatically. Please contact support.`
        );
        return;
      }

      const { patientId, bookingPayload, booking_type, consultationFee } =
        JSON.parse(pendingRaw);

      // Complete booking
      const bookRes = await bookSlot(bookingPayload);

      // Mark payment as paid in the backend
      await updatePaymentStatus({
        patient_id: String(patientId),
        doctor_id: bookingPayload.doctor_id,
        booking_type: booking_type,
        paid_amount: consultationFee,
        reference_id: orderId,
        booking_date_time: bookingPayload.booking_date_time,
        next_token: bookRes.token,
        bill_head_id: bookRes.bill_head_id,
        paid_status: 1,
      });

      sessionStorage.removeItem("hdfcPendingBooking");
      setStatus("success");
      setMessage(bookRes.message || "Payment successful. Booking confirmed!");

      // Auto-redirect to home after 3 seconds
      setTimeout(() => {
        window.location.href = window.location.origin;
      }, 3000);
    } catch (err) {
      console.error("[PaymentCallback] Error:", err);
      setStatus("failed");
      setMessage(
        err?.message ||
          "An error occurred while completing your booking. Please contact support."
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-200 via-cyan-200 to-teal-200 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === "verifying" && (
          <>
            <Loader message="Verifying payment..." />
            <p className="mt-4 text-gray-600 dark:text-gray-300">
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-xl font-bold text-green-600 mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-600 dark:text-gray-300">{message}</p>
            <p className="text-sm text-gray-400 mt-3">
              Redirecting you back in 3 seconds...
            </p>
          </>
        )}

        {status === "failed" && (
          <>
            <div className="text-red-500 text-6xl mb-4">✗</div>
            <h2 className="text-xl font-bold text-red-600 mb-2">
              Payment Not Completed
            </h2>
            <p className="text-gray-600 dark:text-gray-300">{message}</p>
            <button
              onClick={() => (window.location.href = window.location.origin)}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
